import React from 'react';
import { cn } from '@utils/cn';

export interface TabItem {
  id: string;
  name: string;
  leftIcon?: React.ReactNode;
  count?: number;
  /** Optional suffix rendered after the count inside the badge, e.g. "Now". */
  countLabel?: string;
  disabled?: boolean;
  hasError?: boolean;
}

export interface TabProps {
  tabs: TabItem[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  className?: string;
  variant?: 'default' | 'pills' | 'underline';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}

export const Tab: React.FC<TabProps> = ({
  tabs,
  activeTab,
  onTabChange,
  className = '',
  variant = 'underline',
  size = 'md',
  fullWidth = false,
}) => {
  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'text-xs px-3 py-2';
      case 'md':
        return 'text-sm px-4 py-3';
      case 'lg':
        return 'text-base px-6 py-4';
      default:
        return 'text-sm px-4 py-3';
    }
  };

  const getVariantClasses = () => {
    switch (variant) {
      case 'pills':
        return {
          container:
            'flex space-x-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg',
          tab: 'flex items-center justify-center rounded-md px-3 py-2 text-sm font-medium transition-all duration-200',
          active:
            'bg-white dark:bg-gray-700 rounded-md text-gray-900 dark:text-white shadow-sm',
          inactive:
            'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white',
        };
      case 'underline':
        return {
          container: 'flex border-b-2 border-gray-200 dark:border-gray-600',
          tab: 'flex items-center px-3 py-2 text-sm font-medium transition-all duration-200 border-b-2 border-transparent',
          active:
            'text-indigo-600 dark:text-indigo-50 border-indigo-600 dark:border-indigo-400',
          inactive:
            'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:border-gray-300 dark:hover:border-gray-600',
        };
      default:
        return {
          container: 'flex space-x-1',
          tab: 'flex items-center justify-center px-3 py-2 text-sm font-medium transition-all duration-200 rounded-md',
          active:
            'bg-indigo-100 dark:bg-indigo-900 rounded-md text-indigo-700 dark:text-indigo-300',
          inactive:
            'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700',
        };
    }
  };

  const variantClasses = getVariantClasses();
  const sizeClasses = getSizeClasses();

  return (
    <div className={cn('w-full', className)}>
      <div
        className={cn(
          variantClasses.container,
          fullWidth ? 'w-full' : 'justify-start',
          // Scroll horizontally instead of clipping when tabs exceed the width.
          'overflow-x-auto no-scrollbar',
          'transition-colors duration-200',
        )}>
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          const isDisabled = tab.disabled;
          const hasError = tab.hasError;

          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => !isDisabled && onTabChange(tab.id)}
              disabled={isDisabled}
              className={cn(
                'flex items-center gap-2 transition-all duration-200',
                sizeClasses,
                fullWidth ? 'flex-1' : 'flex-shrink-0',
                isActive ? variantClasses.active : variantClasses.inactive,
                isDisabled && 'opacity-50 cursor-not-allowed',
                !isDisabled && 'cursor-pointer',
                hasError && !isActive && 'text-red-600 dark:text-red-400 border-red-500 dark:border-red-400',
              )}>
              {/* Left Icon */}
              {tab.leftIcon && (
                <div className="flex-shrink-0">{tab.leftIcon}</div>
              )}

              {/* Tab Name */}
              <span className="font-medium">{tab.name}</span>

              {/* Count Badge */}
              {tab.count !== undefined && (
                <div className="flex-shrink-0">
                  <span
                    className={cn(
                      'inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full',
                      isActive
                        ? 'bg-indigo-100 dark:bg-gray-700 text-indigo-700 dark:text-indigo-200'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
                    )}>
                    {tab.countLabel ? `${tab.count} ${tab.countLabel}` : tab.count}
                  </span>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
